import { createSlice } from '@reduxjs/toolkit';

const normalizePosition = (pos) => {
  if (!pos) return pos;
  return {
    ...pos,
    id:          pos.id          || null,
    instrumentId: pos.instrumentId || '',
    qty:         Number(pos.qty)        ?? 0,
    avgPrice:    Number(pos.avgPrice)   ?? 0,
    cost:        Number(pos.cost)       ?? 0,
    sl:          Number(pos.sl)         || 0,
    tp:          Number(pos.tp)         || 0,
    swap:        Number(pos.swap)       || 0,
    commission:  Number(pos.commission) || 0,
    margin:      Number(pos.margin)     || 0,
    openTime:    pos.openTime           || null,
    direction:   pos.direction          || (Number(pos.qty) >= 0 ? 'buy' : 'sell'),
    category:    pos.category           || '',
  };
};

const normalizePortfolio = (portfolio) => {
  const normalized = {};
  Object.entries(portfolio).forEach(([key, pos]) => {
    normalized[key] = normalizePosition(pos);
  });
  return normalized;
};

const normalizeHistoryOrder = (order) => {
  if (!order) return order;
  return {
    ...order,
    id:          order.id          || null,
    qty:         Number(order.qty)         || 0,
    pnl:         Number(order.pnl)         || 0,
    swap:        Number(order.swap)        || 0,
    commission:  Number(order.commission)  || 0,
    entryPrice:  Number(order.entryPrice)  || 0,
    exitPrice:   Number(order.exitPrice)   || 0,
    sl:          Number(order.sl)          || 0,
    tp:          Number(order.tp)          || 0,
    closeReason: order.closeReason         || 'WebTrader',
    openTime:    order.openTime            || order.time || null,
    closeTime:   order.closeTime           || null,
  };
};

const initialState = {
  balance:       0,
  portfolio:     {},
  pendingOrders: [],
  historyOrders: [],
  totalProfit:   0,
  totalTrades:   0,
  marketData:    {},
  loading:       false,
  error:         null,
};

const tradeSlice = createSlice({
  name: 'trade',
  initialState,
  reducers: {
    setBalance: (state, action) => {
      state.balance = Number(action.payload) || 0;
    },

    setPortfolio: (state, action) => {
      state.portfolio = normalizePortfolio(action.payload);
    },

    updatePosition: (state, action) => {
      const { instrumentId, position } = action.payload;
      const normalized = normalizePosition(position);
      const key = normalized.id || instrumentId;
      
      if (normalized && Math.abs(normalized.qty) > 0.001) {
        state.portfolio[key] = normalized;
      } else {
        delete state.portfolio[key];
      }
    },

    removePosition: (state, action) => {
      delete state.portfolio[action.payload];
    },

    setPendingOrders: (state, action) => {
      state.pendingOrders = action.payload;
    },

    addPendingOrder: (state, action) => {
      state.pendingOrders.push(action.payload);
    },

    removePendingOrder: (state, action) => {
      state.pendingOrders = state.pendingOrders.filter(o => o.id !== action.payload);
    },

    setHistoryOrders: (state, action) => {
      state.historyOrders = action.payload.map(normalizeHistoryOrder);
    },

    addHistoryOrder: (state, action) => {
      state.historyOrders.unshift(normalizeHistoryOrder(action.payload));
      if (state.historyOrders.length > 100) state.historyOrders = state.historyOrders.slice(0, 100);
    },

    setTotalProfit: (state, action) => {
      state.totalProfit = Number(action.payload) || 0;
    },

    setTotalTrades: (state, action) => {
      state.totalTrades = Number(action.payload) || 0;
    },

    updateMarketData: (state, action) => {
      state.marketData = { ...state.marketData, ...action.payload };
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    batchUpdateMarketData: (state, action) => {
      action.payload.forEach(update => {
        state.marketData[update.symbol] = update.data;
      });
    },
  },
});

export const {
  setBalance, setPortfolio, updatePosition, removePosition,
  setPendingOrders, addPendingOrder, removePendingOrder,
  setHistoryOrders, addHistoryOrder,
  setTotalProfit, setTotalTrades,
  updateMarketData, batchUpdateMarketData,
  setLoading, setError, clearError,
} = tradeSlice.actions;

export default tradeSlice.reducer;